/**
 * Guiones del asistente del sitio demo (cero LLM / cero tokens).
 * Copy en español de México, tuteo. Bien explicativo por vista.
 */

export type CoachScene =
  | 'projects'
  | 'workspace'
  | 'quote'
  | 'context'
  | 'apu'
  | 'other';

export type CoachTip = {
  scene: CoachScene;
  title: string;
  /** Párrafo introductorio. */
  intro: string;
  /** Pasos / puntos a recorrer en esta vista. */
  bullets: string[];
  /** Texto del pill «Siguiente» (navegable si hay nextScene). */
  nextHint?: string;
  /** Destino del pill (lo resuelve DemoGuide con los IDs del ejemplo). */
  nextScene?: CoachScene;
};

export function resolveCoachScene(pathname: string): CoachScene {
  if (pathname === '/projects' || pathname === '/') return 'projects';
  if (pathname.includes('/obra-context')) return 'context';
  if (pathname.includes('/apu-ia')) return 'apu';
  if (/\/quotes\/[^/]+$/.test(pathname) && !pathname.includes('/apu-ia')) return 'quote';
  if (/^\/project\/[^/]+$/.test(pathname)) return 'workspace';
  return 'other';
}

export const COACH_TIPS: Record<CoachScene, CoachTip> = {
  projects: {
    scene: 'projects',
    title: 'Lista de proyectos — punto de partida',
    intro:
      'En OPUS ai cada concurso u obra es un proyecto. Este sitio es solo demostración: verás un proyecto de muestra ya resuelto («Casa habitación — Ejemplo»). En tu cuenta real el flujo empieza aquí.',
    bullets: [
      'En tu cuenta: creas un proyecto nuevo (nombre de la obra o concurso).',
      'Luego entras al proyecto para subir documentos (bases, memoria, listado de conceptos, etc.).',
      'OPUS ai analiza e indexa esos archivos: de ahí salen el resumen del concurso, las respuestas del chat y la hoja de presupuesto.',
      'Aquí en el demo: abre el proyecto con la etiqueta «Ejemplo». No hace falta crear nada ni subir archivos; todo ya está listo para recorrer.',
    ],
    nextHint: 'Haz clic en «Casa habitación — Ejemplo» para entrar al workspace del proyecto.',
    nextScene: 'workspace',
  },

  workspace: {
    scene: 'workspace',
    title: 'Dentro del proyecto — documentos, resumen y chat',
    intro:
      'Esta es la “casa” de la obra. En una cuenta real aquí arrastras o abres los PDF/Excel del concurso; el sistema los clasifica, los analiza y deja el material listo para presupuestar. En el ejemplo esos pasos ya ocurrieron: explora cada pestaña para entender el flujo.',
    bullets: [
      'Pestaña Documentos: aquí se arrastran o se eligen los archivos (convocatoria, especificaciones, listado de conceptos, contrato…). Cada uno se clasifica (bases, memoria, catálogo, etc.) y se indexa. Sin documentos no hay resumen ni presupuesto útil.',
      'Importante — lista de conceptos: debes marcar / seleccionar el archivo que es el listado de conceptos del concurso (la plantilla con claves, unidades y cantidades). Ese archivo es la base para armar la hoja de presupuesto. En el demo ya viene identificado.',
      'Pestaña Resumen del concurso: OPUS ai resume plazo, anticipo, objeto de la obra y puntos clave a partir de los documentos indexados. En el demo el resumen ya está generado.',
      'Pestaña Preguntas: haces preguntas en lenguaje natural sobre tus documentos. En el demo las respuestas son de muestra; en tu cuenta usan tu plan.',
      'Botón Contexto de proyecto: dónde está la obra, de qué tipo es y en qué condiciones se trabaja. Es el siguiente paso, y no es un trámite: de ahí sale el ajuste de rendimientos que después mueve los precios.',
      'Botón Abrir presupuesto: pasa a la hoja de conceptos. En tu cuenta, si aún no hay hoja, primero se lee el listado de conceptos que seleccionaste.',
    ],
    nextHint:
      'Recorre las tres pestañas (Documentos → Resumen → Preguntas). Luego pulsa «Contexto de proyecto».',
    nextScene: 'context',
  },

  context: {
    scene: 'context',
    title: 'Contexto de proyecto — lo que hace que el precio sea de TU obra',
    intro:
      'Dos obras idénticas en el papel no cuestan lo mismo al nivel del mar que a 2,800 metros de altura, ni en un terreno despejado que en un sótano. Acá le dices a OPUS ai en qué condiciones se va a trabajar, y con eso ajusta los rendimientos antes de calcular un solo peso.',
    bullets: [
      'Revisa ciudad y estado, disciplina, tipo de obra, altitud y condiciones del sitio. En el demo es solo lectura: nada se guarda.',
      'Por eso va antes del presupuesto: los precios unitarios que OPUS ai construya nacen con estos rendimientos. Si esto queda vacío, salen genéricos.',
      'En tu cuenta es un requisito: sin contexto capturado, OPUS ai no genera precios unitarios nuevos.',
      'Y si más adelante cambias el contexto, conviene volver a generar los precios que ya habías armado para que no queden desfasados.',
    ],
    nextHint: 'Ve al presupuesto: la hoja de conceptos con sus precios.',
    nextScene: 'quote',
  },

  quote: {
    scene: 'quote',
    title: 'Presupuesto — la hoja de conceptos y de dónde sale cada precio',
    intro:
      'Ya dijiste qué obra es y en qué condiciones se trabaja; ahora toca ponerle precio. En el demo los pasos 1 y 2 están hechos (por eso los ves apagados) y el 3 lo puedes recorrer en solo lectura.',
    bullets: [
      'Paso 1 · Armar la hoja: lee el archivo del listado de conceptos que marcaste en Documentos. Sin ese archivo no hay partidas ni cantidades.',
      'Paso 2 · Buscar equivalentes: eliges uno o más catálogos de referencia (por ejemplo ECOSTOS) y los ordenas por prioridad — el primero manda cuando hay varios candidatos. OPUS ai compara cada concepto contra tus catálogos y, en la misma búsqueda, confirma los dudosos con inteligencia artificial. Lo que no está en tus catálogos pasa al paso 3.',
      'Paso 3 · análisis con IA: solo para los conceptos que no encontraron equivalente. OPUS ai arma el análisis de precio unitario —material, mano de obra, equipo, herramienta y auxiliares— con los rendimientos ya ajustados al contexto que capturaste. Ocurre aquí mismo, en esta pantalla: no hay que ir a ninguna otra parte.',
      'Recorre la hoja: cada concepto dice de dónde salió su precio, y puedes abrirlo para ver el desglose completo — incluidos los que armó la inteligencia artificial, con sus insumos, rendimientos y subtotales. En el demo ya hay varios listos para revisar.',
    ],
    // Sin `nextScene`: el recorrido termina acá y lo que sigue es abrir un concepto, no
    // cambiar de pantalla. Antes invitaba a "entrar al paso 3", que llevaba a una vista
    // aparte que ya no se usa: los análisis se generan y se revisan en ESTA.
    nextHint: 'Abre un concepto de la hoja y mira su desglose de arriba a abajo. Los que armó la inteligencia artificial lo dicen.',
  },

  apu: {
    scene: 'apu',
    title: 'análisis con IA — precio unitario cuando no hay catálogo',
    intro:
      'Si un concepto no tiene equivalente en tus catálogos, OPUS ai propone un análisis de precio unitario completo a costo directo. En el demo ya hay varios conceptos analizados con IA listos para inspeccionar.',
    bullets: [
      'Abre un concepto de la lista: verás el desglose del precio unitario — materiales, mano de obra, equipo, herramienta y auxiliares, con rendimientos y subtotales.',
      'De dónde salen los precios: primero de los catálogos que elegiste, por prioridad. Cada análisis te dice cuántos de sus insumos salieron de tu catálogo; los que no estaban ahí toman un precio de mercado, para no dejar ningún renglón en cero.',
      'En tu cuenta: revisas, ajustas si hace falta, apruebas o rechazas. Aquí es solo lectura.',
      'El objetivo: no dejar conceptos sin precio unitario; lo que el catálogo no cubre lo construye OPUS ai y tú lo validas.',
    ],
    nextHint: 'Elige un concepto analizado con IA y revisa su matriz de insumos de arriba a abajo.',
    // Sin nextScene: aquí el siguiente es explorar la matriz, no otra pantalla.
  },

  other: {
    scene: 'other',
    title: 'Sigue el recorrido del ejemplo',
    intro:
      'Esta pantalla no es el eje del demo. El recorrido útil pasa por el proyecto de ejemplo: documentos → resumen → preguntas → contexto de proyecto → presupuesto → análisis con IA.',
    bullets: [
      'Usa la barra de pasos de arriba o vuelve a Proyectos.',
      'Abre «Casa habitación — Ejemplo» y sigue las pestañas en orden.',
    ],
    nextHint: 'Ve a Proyectos y entra al ejemplo.',
    nextScene: 'projects',
  },
};

/** Preguntas sugeridas del chat demo (respuestas fijas, etiquetadas). */
export type DemoChatSample = {
  question: string;
  answer: string;
};

export const DEMO_CHAT_SAMPLES: DemoChatSample[] = [
  {
    question: '¿Cuál es el plazo de ejecución de la obra?',
    answer:
      'Según la carátula del catálogo, el plazo de ejecución es de 90 días naturales. La obra es privada ' +
      '(contratación directa entre particulares), así que el plazo corre a partir de lo que pacten las partes. ' +
      '(Respuesta de demostración — en una obra tuya el asistente la obtendría de tus documentos indexados.)',
  },
  {
    question: '¿Qué incluyen los precios unitarios?',
    answer:
      'Los precios se cotizan a pie de obra e incluyen materiales, mano de obra, herramienta, equipo, indirectos, ' +
      'financiamiento y utilidad. NO incluyen: el costo del terreno, licencias, proyecto arquitectónico, supervisión externa ' +
      'ni las acometidas definitivas de luz y agua. Los importes se expresan sin IVA, con precios de referencia de julio de 2026. ' +
      '(Respuesta de demostración — sin consumo de inteligencia artificial.)',
  },
  {
    question: '¿Qué sistema constructivo usa la vivienda?',
    answer:
      'Casa habitación de un nivel, tipo económico, de 65.00 m² construidos sobre un terreno de 160.00 m² (8.00 × 20.00 m): ' +
      'cimentación corrida de mampostería de piedra braza con dalas de desplante, muros de tabique rojo recocido confinados ' +
      'con castillos y dalas de cerramiento, y losa maciza de concreto de 12 cm. Incluye dos recámaras, baño completo, cocina, ' +
      'estancia-comedor y patio de servicio. ' +
      '(Respuesta de demostración con datos del expediente de ejemplo.)',
  },
];
